import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Icon,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import useUserStore from "store/zustand/user";
import {
  resendVerifyCodeUserAPI,
  verifyUserAPI,
} from "store/services/user.service";
import { useTranslation } from "react-i18next";
import useLocalizationStore from "store/zustand/localization";
import { useNavigate } from "react-router-dom";
import { getSvgByLanguage } from "components/Flag";
import { createAlert } from "store/utils/alertUtils";

const CODE_LENGTH = 4;

const Verification = () => {
  const { setLanguage, language } = useLocalizationStore();
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState(language || "en");
  const { t, i18n } = useTranslation();

  const handleLanguageChange = async (lang) => {
    setSelectedLanguage(lang);
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
  const [submitting, setSubmitting] = useState(false);
  const { email, setUser, setToken, setIsSubscription } = useUserStore();
  const inputRefs = useRef([]);

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    const nextCode = [...code];
    nextCode[index] = value;
    setCode(nextCode);

    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const otp = code.join("");
    if (otp.length !== CODE_LENGTH) {
      return;
    }

    try {
      setSubmitting(true);

      const res = await verifyUserAPI({ email, code: otp });
      const userData = res?.data?.body;
      if (res?.data?.isSuccess) {
        setToken(userData.access_token);

        setIsSubscription(userData.subscriptionStatus);
        setUser(userData.user);
        navigate("/");
        createAlert({
          type: "success",
          status: true,
          notification: {
            title: res?.data?.message,
            body: res?.data?.message,
            titleArabic: res?.data?.message,
            bodyArabic: res?.data?.message,
          },
        });
      } else {
        createAlert({
          type: "error",
          status: true,
          notification: {
            title: res?.data?.message,
            body: res?.data?.message,
            titleArabic: res?.data?.message,
            bodyArabic: res?.data?.message,
          },
        });
      }
    } catch (error) {
      createAlert({
        type: "error",
        status: true,
        notification: {
          title: error?.message,
          body: error?.message,
          titleArabic: error?.message,
          bodyArabic: error?.message,
        },
      });
      console.log("Verifying OTP:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    await resendVerifyCodeUserAPI({ email });
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url('/images/bg-image-3.jpg')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.3)",
          zIndex: 1,
        },
      }}
    >
      <Box
        component="nav"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 4,
          py: 2,
          zIndex: 2,
          color: "common.white",
        }}
      >
        <Box
          component="img"
          src="/icon/mausool.png"
          alt="Mausool logo"
          onClick={() => navigate("/")}
          sx={{ height: 40, cursor: "pointer" }}
        />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton size={"medium"} aria-label="flag">
            <Icon sx={{ width: "1.5rem", height: "1.5rem" }} aria-label="flag">
              {getSvgByLanguage(selectedLanguage)}
            </Icon>
          </IconButton>

          <FormControl
            size="small"
            name="language"
            aria-label="language"
            sx={{
              border: "none",
              outline: "none",
            }}
          >
            <Select
              value={selectedLanguage}
              aria-labelledby="language-select"
              onChange={(event) => handleLanguageChange(event.target.value)}
              MenuProps={{
                disableScrollLock: true,
              }}
              sx={{
                padding: 0,
                minWidth: 0,
                border: "none",
                outline: "none",
                color: "white !important",
                "& .MuiSelect-select": {
                  minWidth: "0 !important",
                  color: "white !important",
                  border: "none",
                },
                "& .MuiOutlinedInput-root": {
                  border: "none",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                },
                "& .MuiSelect-icon": {
                  color: "white !important",
                },
              }}
            >
              <MenuItem value="en">{t("header.language.short.en")}</MenuItem>
              <MenuItem value="ar">{t("header.language.short.ar")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Paper
        elevation={6}
        sx={{
          width: "100%",
          maxWidth: 500,
          p: 4,
          borderRadius: 1,
          zIndex: 2,
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(255, 255, 255, 0.85)",
        }}
      >
        <Typography variant="h5" fontWeight={600} mb={1} textAlign="center">
          {t("auth.verification.title")}
        </Typography>
        <Typography
          variant="body2"
          textAlign="center"
          color="text.secondary"
          mb={3}
        >
          {t("auth.verification.description")}
        </Typography>
        <Box component="form" mt={3} onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
            {code.map((digit, index) => (
              <TextField
                key={index}
                value={digit}
                inputRef={(ref) => (inputRefs.current[index] = ref)}
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: "center", fontSize: "1.5rem" },
                  inputMode: "numeric",
                  "aria-label": t("auth.verification.otpDigit", {
                    index: index + 1,
                  }),
                }}
                onChange={(event) => handleChange(event.target.value, index)}
                onKeyDown={(event) => handleKeyDown(event, index)}
                sx={{ width: 56 }}
              />
            ))}
          </Box>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            loading={submitting}
            sx={{ mt: 2 }}
            disabled={code.some((digit) => !digit) || submitting}
          >
            {t("auth.verification.verify")}
          </Button>
          <Button
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleResend}
            disabled={submitting}
          >
            {t("auth.verification.resend")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Verification;
